import { useCallback, useRef } from 'react'

import { getInsolationBucket }  from '../utils/productInsolation.js'
import { isDisplayable }        from '../utils/productUtils.js'
import { AVAILABILITY_STATUS }  from '../constants/availabilityStatus.js'
import { COLOR_MODE }           from '../constants/colorMode.js'
import { HERO_OUTLINE }         from '../constants/heroOutline.js'
import { OUTLINE_PREVIEW_MODE } from '../constants/outlinePreviewMode.js'
import { PRODUCT_PREVIEW_MODE } from '../constants/productPreviewMode.js'
import { SVG_PRODUCT_CLASS }    from '../constants/svgClasses.js'

/**
 * useSvgProductInteraction
 *
 * Manages imperative event listeners on SVG apartment groups rendered by
 * `TierSvgCanvas` and (future) `Outlines` in Hero. Returns a `wireListeners`
 * function designed to be called from a `useEffect` after the SVG mounts.
 *
 * @param {object}    params
 * @param {object[]}  params.products           - Full list of displayable products
 * @param {object[]}  params.filteredProducts   - Filtered subset (drives coloring)
 * @param {string|null} params.activeProduct    - UUID of the currently active product
 * @param {string}    [params.colorMode='rooms'] - 'rooms' | 'status' | 'insolation' | 'compare' | 'category'
 * @param {Map<string,string>|null} [params.colorModeDeciles] - uuid→decile map, required for COMPARE mode
 * @param {function}  params.onProductClick     - (uuid) => void — navigate to product
 * @param {function}  params.onPreviewChange    - ({product, anchorRect}|null) => void
 * @param {function}  [params.onProductHover]   - (product|null) => void — for legend highlight
 * @param {boolean}   [params.areSoldAccessible=false]
 * @param {boolean}   [params.areReservedAccessible=true]
 * @param {boolean}   [params.highlightSoldInRed=false]
 * @param {{ isMobile: boolean }}  [params.deviceLayout]
 *
 * @returns {{ wireListeners: (containerEl: Element|null) => () => void }}
 */
// ── Pure factory (testable without React) ────────────────────────────────────

/**
 * Creates a `wireListeners` function for product (apartment) SVG interaction.
 *
 * Supports two SVG structures:
 * - **Grouped** (tier SVGs): `g.apartments > g#uuid > path.interior` — events
 *   on the group, styling on the interior path.
 * - **Flat** (outlines_apartments.svg, main.svg): top-level `<path id="uuid">`
 *   without a wrapper group — the path serves as both event target and style target.
 *
 * Both strategies are tried automatically. Grouped matches take priority;
 * flat paths already covered by a group are skipped.
 *
 * @param {object} params — same shape as `useSvgProductInteraction` params
 * @param {object} refs
 * @param {{ current: string|null }}  refs.lastClickedRef
 * @param {{ current: Element|null }} refs.previewedPathRef
 * @returns {(containerEl: Element|null) => () => void}
 */
export function createProductWireListeners(
  {
    products,
    filteredProducts,
    buildings,
    activeProduct,
    colorMode = COLOR_MODE.ROOMS,
    colorModeDeciles = null,
    onProductClick,
    onPreviewChange,
    onProductHover,
    areSoldAccessible     = false,
    areReservedAccessible = true,
    highlightSoldInRed    = false,
    deviceLayout,
    currentFrameRef,
    isDraggingRef,
  },
  refs,
) {
  return (containerEl) => {
    if (!containerEl) return () => {}

    const { lastClickedRef, previewedPathRef } = refs
    const byUuid        = new Map(products.map(p => [p.uuid, p]))
    const filteredUuids = new Set(filteredProducts.map(p => p.uuid))
    const isMobile      = deviceLayout?.isMobile ?? false

    // Returns true when the eventTarget is inside the active g[data-frame] group,
    // or when the SVG has no frame groups (TiersView / flat outline).
    const isInActiveFrame = (el) => {
      if (!currentFrameRef) return true
      const group = el.closest('g[data-frame]')
      if (!group) return true
      return parseInt(group.dataset.frame, 10) === currentFrameRef.current
    }

    // ── Collect product elements from both SVG structures ─────────────────────
    // Each entry: { uuid, path (style target), eventTarget (listener target), tag }
    //
    // 1. Grouped (tier SVGs): g.apartments > g#uuid > path.interior
    //    - eventTarget = group, path = interior, tag = text.tag
    // 2. Flat (outlines_apartments.svg, main.svg): top-level <path id="uuid">
    //    - eventTarget = path itself, path = same element, tag = null

    const items = []
    const seenUuids = new Set()

    // Strategy 1: grouped
    for (const group of containerEl.querySelectorAll('g.apartments > g')) {
      const uuid = group.id
      if (!uuid || seenUuids.has(uuid)) continue
      const interior = group.querySelector('path.interior')
      if (!interior) continue
      seenUuids.add(uuid)
      items.push({ uuid, path: interior, eventTarget: group, tag: group.querySelector('text.tag') })
    }

    // Strategy 2: flat — top-level <path id="uuid"> without a wrapper group.
    // Used in outlines_apartments.svg and main.svg (VisualizationView).
    //
    // IMPORTANT: do NOT add to seenUuids here.
    // In outlines_apartments.svg the same apartment UUID appears in MANY g[data-frame]
    // groups (once per 360° frame - group). Each occurrence is a distinct DOM element that requires
    // independent coloring and event listeners.  Adding to seenUuids would cause only the
    // first frame's paths to be processed — all subsequent frames would be skipped.
    // seenUuids guards only against Strategy 1 / Strategy 2 overlap (prevents processing
    // the same DOM element twice when a grouped path happens to also appear flat).
    for (const el of containerEl.querySelectorAll('path[id]')) {
      const id = el.id
      if (!id) continue
      if (seenUuids.has(id)) continue          // covered by Strategy 1 (grouped)
      if (id.split('-').length < 5) continue   // not a UUID
      items.push({ uuid: id, path: el, eventTarget: el, tag: null })
    }

    // ── Apply coloring + events ───────────────────────────────────────────────
    const controller = new AbortController()
    const { signal } = controller
    const isStatusMode     = colorMode === COLOR_MODE.STATUS
    const isInsolationMode = colorMode === COLOR_MODE.INSOLATION
    const isCompareMode    = colorMode === COLOR_MODE.COMPARE
    const isCategoryMode   = colorMode === COLOR_MODE.CATEGORY

    for (const { uuid, path, eventTarget, tag } of items) {
      const product = byUuid.get(uuid)

      // Reset all state classes first
      path.classList.remove(
        SVG_PRODUCT_CLASS.ROOMS_COLOR,
        SVG_PRODUCT_CLASS.INSOLATION_COLOR,
        SVG_PRODUCT_CLASS.COMPARE_COLOR,
        SVG_PRODUCT_CLASS.CATEGORY_COLOR,
        SVG_PRODUCT_CLASS.STATUS_SOLD,
        SVG_PRODUCT_CLASS.STATUS_AVAILABLE,
        SVG_PRODUCT_CLASS.STATUS_RESERVED,
        SVG_PRODUCT_CLASS.FILTERED_OUT,
        SVG_PRODUCT_CLASS.ACTIVE,
        SVG_PRODUCT_CLASS.PREVIEW,
      )
      // Reset tag highlight
      if (tag) tag.classList.remove(SVG_PRODUCT_CLASS.ACTIVE_TAG)
      // Remove any per-bucket dynamic classes
      for (const cls of Array.from(path.classList)) {
        if (/^rooms-\d+$/.test(cls))    path.classList.remove(cls)
        if (/^insolation-/.test(cls))   path.classList.remove(cls)
        if (/^compare-p\d$/.test(cls))  path.classList.remove(cls)
        if (/^category-/.test(cls))     path.classList.remove(cls)
      }

      if (!product) {
        // Orphan group — path exists in SVG but no matching product in data.
        // Mark as filtered-out so it gets the faded style instead of raw white.
        path.classList.add(SVG_PRODUCT_CLASS.FILTERED_OUT)
        continue
      }

      // Color based on filter + status + colorMode
      const isSold     = product.status === AVAILABILITY_STATUS.SOLD
      const isReserved = product.status === AVAILABILITY_STATUS.RESERVED
      const isVisible  = filteredUuids.has(uuid)
      const forceSoldStatusColor = isSold && (isStatusMode || highlightSoldInRed)

      if (!isVisible) {
        path.classList.add(SVG_PRODUCT_CLASS.FILTERED_OUT)
      } else if (isStatusMode) {
        // ── Status color mode ──────────────────────────────────────────────
        if (isSold) {
          path.classList.add(SVG_PRODUCT_CLASS.STATUS_SOLD)
        } else if (isReserved) {
          path.classList.add(SVG_PRODUCT_CLASS.STATUS_RESERVED)
        } else {
          path.classList.add(SVG_PRODUCT_CLASS.STATUS_AVAILABLE)
        }
      } else if (isInsolationMode) {
        // ── Insolation color mode ──────────────────────────────────────────
        if (forceSoldStatusColor) {
          path.classList.add(SVG_PRODUCT_CLASS.STATUS_SOLD)
        } else {
          const bucket = getInsolationBucket(product.sun_light_ranges)
          path.classList.add(SVG_PRODUCT_CLASS.INSOLATION_COLOR, `insolation-${bucket}`)
        }
      } else if (isCompareMode) {
        // ── Compare (area decile) color mode ──────────────────────────────
        if (forceSoldStatusColor) {
          path.classList.add(SVG_PRODUCT_CLASS.STATUS_SOLD)
        } else {
          const decile = colorModeDeciles?.get(uuid)
          if (decile == null) {
            path.classList.add(SVG_PRODUCT_CLASS.FILTERED_OUT)
          } else {
            path.classList.add(SVG_PRODUCT_CLASS.COMPARE_COLOR, `compare-${decile}`)
          }
        }
      } else if (isCategoryMode) {
        // ── Category color mode ───────────────────────────────────────────
        if (forceSoldStatusColor) {
          path.classList.add(SVG_PRODUCT_CLASS.STATUS_SOLD)
        } else {
          const cat = product.product_category
          if (cat == null) {
            path.classList.add(SVG_PRODUCT_CLASS.FILTERED_OUT)
          } else {
            path.classList.add(SVG_PRODUCT_CLASS.CATEGORY_COLOR, `category-${cat}`)
          }
        }
      } else {
        // ── Rooms color mode (default) ─────────────────────────────────────
        if (forceSoldStatusColor) {
          path.classList.add(SVG_PRODUCT_CLASS.STATUS_SOLD)
        } else {
          path.classList.add(SVG_PRODUCT_CLASS.ROOMS_COLOR)
          if (product.rooms != null) {
            path.setAttribute('data-rooms', String(product.rooms))
            path.classList.add(`rooms-${product.rooms}`)
          }
        }
      }

      if (uuid === activeProduct) {
        path.classList.add(SVG_PRODUCT_CLASS.ACTIVE)
        if (tag) tag.classList.add(SVG_PRODUCT_CLASS.ACTIVE_TAG)
      }


      const productBuilding = buildings?.find(b => b.id_cad === product.building_id);
      const isPhased = productBuilding?.currently_on_sale === false;

      const mode = resolveProductPreviewMode({
        isInOtherStage: isPhased,
        isVisible,
        isSold,
        isReserved,
        visibility: product.visibility,
        areSoldAccessible,
        areReservedAccessible,
        building: productBuilding
      })

      if (!isMobile) {
        eventTarget.addEventListener('mouseover', (e) => {
          if (isDraggingRef?.current) return
          if (!isInActiveFrame(eventTarget)) return
          onPreviewChange?.({
            type: HERO_OUTLINE.APARTMENTS,
            building: productBuilding,
            cursorPos: { x: e.clientX, y: e.clientY }, 
            mode,
            product, 
          })
          onProductHover?.(product)
        }, { signal })

        eventTarget.addEventListener('mouseleave', () => {
          if (!isInActiveFrame(eventTarget)) return
          onPreviewChange?.(null)
          onProductHover?.(null)
        }, { signal })

        // if (mode === PRODUCT_PREVIEW_MODE.NORMAL) {
          eventTarget.addEventListener('click', () => {
            if (!isInActiveFrame(eventTarget)) return
            onProductClick?.(mode, {
              type: HERO_OUTLINE.APARTMENTS,
              building: productBuilding,
              mode,
              product, 
            })
          }, { signal })
        // }
      } else {
        // Double-tap pattern: first tap = preview (hover effect), second = navigate
        eventTarget.addEventListener('click', (e) => {
          if (!isInActiveFrame(path)) return
          if (isDraggingRef?.current) return
          if (lastClickedRef.current === uuid) {
            // Second tap — navigate
            onProductClick?.(mode, {
              type: HERO_OUTLINE.APARTMENTS,
              building: productBuilding,
              mode,
              product, 
            })
            lastClickedRef.current = null
            onPreviewChange?.(null)
          } else {
            // First tap — show preview + hover fill
            lastClickedRef.current = uuid
            onPreviewChange?.({
              type: HERO_OUTLINE.APARTMENTS, 
              anchorRect: path.getBoundingClientRect(),
              building: productBuilding,
              cursorPos: { x: e.clientX, y: e.clientY }, 
              mode,
              product,
              })
          }
        }, { signal })
      }
    }

    const pathsByUuid = new Map()

    items.forEach(({ uuid, path }) => {
      if (!uuid || !path) return
      if (!pathsByUuid.has(uuid)) {
        pathsByUuid.set(uuid, [])
      }
      pathsByUuid.get(uuid).push(path)
    })

    let currentPreviewedUuid = null

    const handlePreviewChangeSync = (e) => {
      const newUuid = e.detail

      if (currentPreviewedUuid && pathsByUuid.has(currentPreviewedUuid)) {
        const prevPaths = pathsByUuid.get(currentPreviewedUuid)
        for (let i = 0; i < prevPaths.length; i++) {
          prevPaths[i].classList.remove(SVG_PRODUCT_CLASS.PREVIEW)
        }
      }

      if (newUuid && pathsByUuid.has(newUuid)) {
        const newPaths = pathsByUuid.get(newUuid)
        for (let i = 0; i < newPaths.length; i++) {
          newPaths[i].classList.add(SVG_PRODUCT_CLASS.PREVIEW)
        }
        lastClickedRef.current = newUuid
        currentPreviewedUuid = newUuid
      } else {
        currentPreviewedUuid = null
      }
    }

    containerEl.addEventListener('bim:preview-change', handlePreviewChangeSync)

    return () => {
      controller.abort()
      containerEl.removeEventListener('bim:preview-change', handlePreviewChangeSync)
      if (previewedPathRef.current) previewedPathRef.current.classList.remove(SVG_PRODUCT_CLASS.PREVIEW)
      previewedPathRef.current = null
      lastClickedRef.current = null
    }
  }
}

// ── Hook (thin React wrapper) ─────────────────────────────────────────────────

export function useSvgProductInteraction({
  products,
  filteredProducts,
  buildings,
  activeProduct,
  previewedProductUuid = null,
  colorMode = COLOR_MODE.ROOMS,
  colorModeDeciles = null,
  onProductClick,
  onPreviewChange,
  onProductHover,
  areSoldAccessible     = false,
  areReservedAccessible = true,
  highlightSoldInRed    = false,
  deviceLayout,
  currentFrameRef,
  isDraggingRef,
}) {
  const lastClickedRef   = useRef(null)
  const previewedPathRef = useRef(null)

  const wireListeners = useCallback(
    (containerEl) => createProductWireListeners(
      { 
        products, 
        filteredProducts, 
        buildings, 
        activeProduct, 
        colorMode, 
        colorModeDeciles, 
        onProductClick, 
        onPreviewChange, 
        onProductHover, 
        areSoldAccessible, 
        areReservedAccessible, 
        highlightSoldInRed, 
        deviceLayout, 
        currentFrameRef, 
        isDraggingRef 
      },
      { lastClickedRef, previewedPathRef },
    )(containerEl),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      products, 
      filteredProducts, 
      buildings, 
      activeProduct, 
      colorMode, 
      colorModeDeciles, 
      onProductClick, 
      onPreviewChange, 
      onProductHover, 
      areSoldAccessible, 
      areReservedAccessible, 
      highlightSoldInRed, 
      deviceLayout
    ],
    // currentFrameRef and isDraggingRef are stable ref objects — intentionally excluded from deps
  )

  return { wireListeners }
}

export function resolveProductPreviewMode({
  isInOtherStage = false,
  building,
  isVisible,
  isSold,
  isReserved,
  visibility,
  areSoldAccessible,
  areReservedAccessible,
}) {

  if ((building && building.currently_on_sale === false) || isInOtherStage) {
    if (building.allow_inquiry)  return OUTLINE_PREVIEW_MODE.OTHER_STAGE_INQUIRY
    if (building.redirect_to_app) return OUTLINE_PREVIEW_MODE.OTHER_STAGE_REDIRECT
    return OUTLINE_PREVIEW_MODE.OTHER_STAGE
  }

  if (!isVisible && !isDisplayable(visibility)) return PRODUCT_PREVIEW_MODE.HIDDEN
  if (!isVisible && isSold) return PRODUCT_PREVIEW_MODE.FILTERED_SOLD
  if (!isVisible) return PRODUCT_PREVIEW_MODE.FILTERED
  if (isSold && !areSoldAccessible) return PRODUCT_PREVIEW_MODE.SOLD
  if (isReserved && !areReservedAccessible) return PRODUCT_PREVIEW_MODE.SOLD
  return PRODUCT_PREVIEW_MODE.NORMAL
}
